import { useCallback } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import type { EChartsInstance } from 'echarts-for-react/lib/types';
import { echarts, BRAND_COLOR, COLOR_PALETTE } from '../shared/echarts-config';
import { EmptyState } from '../shared/EmptyState';
import type { CoverageStateData } from '../../../types/analytics.types';
import { STATE_COORDINATES } from './state-coordinates';

type CoverageMapProps = {
  data: CoverageStateData[];
  totalProviders: number;
  onStateClick: (stateCode: string) => void;
};

const buildScatterData = (states: CoverageStateData[]) =>
  states
    .filter((s) => STATE_COORDINATES[s.stateCode])
    .map((s) => ({
      name: s.stateName,
      value: [
        STATE_COORDINATES[s.stateCode][0],
        STATE_COORDINATES[s.stateCode][1],
        s.providerCount,
      ],
      stateCode: s.stateCode,
      categories: s.categories,
    }));

const buildMapOption = (data: CoverageStateData[]) => {
  const scatterData = buildScatterData(data);
  const maxCount = Math.max(...data.map((s) => s.providerCount), 1);
  const minBubble = 8;
  const maxBubble = 45;

  return {
    tooltip: {
      show: true,
      trigger: 'item' as const,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e5e7eb',
      borderWidth: 1,
      textStyle: { color: '#374151', fontSize: 13 },
      formatter: (params: {
        seriesIndex?: number;
        data?: {
          name: string;
          stateCode: string;
          value: number[];
          categories: CoverageStateData['categories'];
        };
      }) => {
        if (params.seriesIndex !== 1) return '';
        const d = params.data;
        if (!d?.categories || !d.value) return '';
        const catLines = d.categories
          .slice(0, 5)
          .map((c) => `${c.categoryName}: ${c.count}`)
          .join('<br/>');
        return [
          `<strong>${d.name} (${d.stateCode})</strong>`,
          `Providers: ${d.value[2].toLocaleString('en-US')}`,
          catLines,
        ]
          .filter(Boolean)
          .join('<br/>');
      },
    },
    geo: {
      map: 'USA',
      roam: false,
      silent: true,
      itemStyle: {
        areaColor: '#f3f4f6',
        borderColor: '#d1d5db',
        borderWidth: 0.5,
      },
      emphasis: { disabled: true },
    },
    series: [
      {
        name: 'base',
        type: 'map',
        map: 'USA',
        geoIndex: 0,
        data: [],
        silent: true,
        tooltip: { show: false },
      },
      {
        name: 'providers',
        type: 'scatter',
        coordinateSystem: 'geo',
        data: scatterData,
        symbolSize: (val: number[]) => {
          const ratio = val[2] / maxCount;
          return minBubble + ratio * (maxBubble - minBubble);
        },
        itemStyle: {
          color: BRAND_COLOR,
          opacity: 0.6,
          borderColor: BRAND_COLOR,
          borderWidth: 1,
        },
        emphasis: {
          itemStyle: {
            opacity: 0.85,
            shadowBlur: 10,
            shadowColor: 'rgba(0,0,0,0.3)',
          },
        },
        label: {
          show: true,
          formatter: (p: { data?: { value?: number[] } }) =>
            (p.data?.value?.[2] ?? 0) > maxCount * 0.15
              ? String(p.data!.value![2])
              : '',
          color: '#fff',
          fontSize: 11,
          fontWeight: 'bold' as const,
        },
      },
    ],
    visualMap: {
      show: false,
      min: 0,
      max: maxCount,
      inRange: { color: [...COLOR_PALETTE].reverse() },
    },
  };
};

export const CoverageMap = ({
  data,
  totalProviders,
  onStateClick,
}: CoverageMapProps) => {
  const handleChartReady = useCallback(
    (instance: EChartsInstance) => {
      instance.on('click', 'series.scatter', (params: Record<string, unknown>) => {
        const d = params.data as { stateCode?: string } | undefined;
        if (d?.stateCode) onStateClick(d.stateCode);
      });
    },
    [onStateClick],
  );

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4">
          Provider Coverage Map
        </h3>
        <EmptyState message="No providers found for this filter combination. Try adjusting your category or vetting status." />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            Provider Coverage Map
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Service provider density by state
          </p>
        </div>
        <div className="text-right">
          <p className="text-3xl font-bold text-[#3d6560]">
            {totalProviders.toLocaleString('en-US')}
          </p>
          <p className="text-xs text-gray-500">Total Providers</p>
        </div>
      </div>
      <ReactEChartsCore
        echarts={echarts}
        option={buildMapOption(data)}
        style={{ height: 460 }}
        onChartReady={handleChartReady}
        notMerge
        lazyUpdate
      />
      <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
        <span>Circle Size = Provider Count</span>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <div className="w-6 h-6 rounded-full bg-[#3d6560] opacity-60" />
            <span>High</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded-full bg-[#3d6560] opacity-60" />
            <span>Med</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-full bg-[#3d6560] opacity-60" />
            <span>Low</span>
          </div>
        </div>
      </div>
    </div>
  );
};
